import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Link,
  Paper,
  Skeleton,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { LineChart } from "@mui/x-charts/LineChart";
import { BarChart } from "@mui/x-charts/BarChart";
import RefreshIcon from "@mui/icons-material/Refresh";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import HubOutlinedIcon from "@mui/icons-material/HubOutlined";
import FacebookIcon from "@mui/icons-material/Facebook";
import InstagramIcon from "@mui/icons-material/Instagram";
import GoogleIcon from "@mui/icons-material/Google";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import PercentIcon from "@mui/icons-material/Percent";
import MouseOutlinedIcon from "@mui/icons-material/MouseOutlined";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import AutoGraphOutlinedIcon from "@mui/icons-material/AutoGraphOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AppShell from "../components/AppShell";
import ExportButton from "../components/ExportButton";
import ApiService from "../services/ApiService";
import InsightsService from "../services/InsightsService";

// ── helpers ───────────────────────────────────────────────────────────────────

function fmt(value) {
  const n = Number(value || 0);
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}

function fmtPct(value) {
  return `${Number(value || 0).toFixed(2)}%`;
}

function fmtDateTime(value) {
  if (!value) return "Never";
  try {
    return new Date(value).toLocaleString(undefined, {
      month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return String(value); }
}

const WINDOWS = [
  { label: "7d", value: "7d" },
  { label: "30d", value: "30d" },
  { label: "90d", value: "90d" },
];

const PROVIDER_COLORS = {
  instagram_business: { color: "#E1306C", bg: "#FFF0F5" },
  facebook_page: { color: "#1877F2", bg: "#EFF6FF" },
  facebook_ads_account: { color: "#1877F2", bg: "#EFF6FF" },
  ga4_property: { color: "#EA4335", bg: "#FEF2F2" },
  default: { color: "#5B5BD6", bg: "#EEF2FF" },
};

function providerStyle(kind) {
  return PROVIDER_COLORS[kind] || PROVIDER_COLORS.default;
}

// ── sub-components ────────────────────────────────────────────────────────────

function ProviderAvatar({ kind, src, size = 32 }) {
  const { color, bg } = providerStyle(kind);
  const icon =
    kind === "instagram_business" ? (
      <InstagramIcon sx={{ color, fontSize: size * 0.55 }} />
    ) : kind === "ga4_property" ? (
      <GoogleIcon sx={{ color, fontSize: size * 0.55 }} />
    ) : (
      <FacebookIcon sx={{ color, fontSize: size * 0.55 }} />
    );
  return (
    <Avatar src={src || undefined} sx={{ width: size, height: size, bgcolor: bg }}>
      {!src ? icon : null}
    </Avatar>
  );
}

function KpiCard({ icon, label, value, sub, accentColor = "#5B5BD6", accentBg = "#EEF2FF", trend }) {
  const trendPositive = trend > 0;
  const trendNeutral = trend === 0 || trend == null;
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        height: "100%",
        position: "relative",
        overflow: "hidden",
        transition: "box-shadow 0.2s",
        "&:hover": { boxShadow: "0px 4px 16px rgba(15,23,42,0.08)" },
      }}
    >
      {/* Accent top bar */}
      <Box
        sx={{
          position: "absolute",
          top: 0, left: 0, right: 0,
          height: 3,
          background: `linear-gradient(90deg, ${accentColor}, ${alpha(accentColor, 0.4)})`,
          borderRadius: "12px 12px 0 0",
        }}
      />
      <Stack spacing={2}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Avatar
            variant="rounded"
            sx={{ bgcolor: accentBg, color: accentColor, width: 40, height: 40 }}
          >
            {icon}
          </Avatar>
          {!trendNeutral && (
            <Chip
              size="small"
              icon={trendPositive ? <TrendingUpIcon sx={{ fontSize: "13px !important" }} /> : <TrendingDownIcon sx={{ fontSize: "13px !important" }} />}
              label={`${trendPositive ? "+" : ""}${trend}%`}
              sx={{
                height: 22,
                fontSize: "0.7rem",
                bgcolor: trendPositive ? "#F0FDF4" : "#FFF1F2",
                color: trendPositive ? "#10B981" : "#EF4444",
                border: "none",
                "& .MuiChip-icon": { color: "inherit" },
              }}
            />
          )}
        </Stack>
        <Box>
          <Typography variant="overline" sx={{ color: "text.secondary", display: "block", mb: 0.25 }}>
            {label}
          </Typography>
          <Typography sx={{ fontSize: "1.75rem", fontWeight: 700, lineHeight: 1.15, letterSpacing: "-0.02em", color: "text.primary" }}>
            {value}
          </Typography>
          {sub && (
            <Typography variant="caption" sx={{ color: "text.secondary", mt: 0.25, display: "block" }}>
              {sub}
            </Typography>
          )}
        </Box>
      </Stack>
    </Paper>
  );
}

function SectionLabel({ children }) {
  return (
    <Typography
      variant="overline"
      sx={{ color: "text.secondary", display: "block", mb: 1.5, fontSize: "0.7rem" }}
    >
      {children}
    </Typography>
  );
}

function EmptyDashboard() {
  return (
    <Paper
      sx={{
        p: 8,
        textAlign: "center",
        background: "linear-gradient(135deg, #F8FAFC 0%, #EEF2FF 100%)",
        border: "1px dashed #C7D2FE",
      }}
    >
      <Box
        sx={{
          width: 64,
          height: 64,
          borderRadius: "16px",
          bgcolor: "#EEF2FF",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mx: "auto",
          mb: 2.5,
        }}
      >
        <HubOutlinedIcon sx={{ fontSize: 30, color: "#5B5BD6" }} />
      </Box>
      <Typography variant="h3" sx={{ mb: 1 }}>No channels connected yet</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 420, mx: "auto", lineHeight: 1.65 }}>
        Connect Facebook + Instagram or Google Analytics to see your reach, engagement, ad performance, and top content — all in one place.
      </Typography>
      <Button
        component={RouterLink}
        to="/integrations"
        variant="contained"
        size="large"
        endIcon={<ArrowForwardIcon />}
        sx={{ mt: 3.5 }}
      >
        Connect channels
      </Button>
    </Paper>
  );
}

function AccountStatusDot({ status }) {
  return (
    <Box
      sx={{
        width: 7,
        height: 7,
        borderRadius: "50%",
        bgcolor: status === "active" ? "#10B981" : status === "error" ? "#EF4444" : "#94A3B8",
        flexShrink: 0,
      }}
    />
  );
}

// ── main view ─────────────────────────────────────────────────────────────────

export default function DashboardView() {
  const navigate = useNavigate();
  const [account, setAccount] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState(null);
  const [window, setWindow] = useState("30d");
  const [chartAccountIdx, setChartAccountIdx] = useState(0);
  const [chartMetric, setChartMetric] = useState("reach");

  const fetchData = useCallback(async () => {
    setError(null);
    try {
      const [me, dashboard] = await Promise.all([
        ApiService.getAccountMe(),
        InsightsService.getDashboard(),
      ]);
      setAccount(me?.account ?? null);
      setData(dashboard ?? null);
    } catch (e) {
      if (e?.response?.status === 401) { navigate("/signin"); return; }
      setError(e?.response?.data?.message || e.message || "Failed to load dashboard.");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  async function handleSyncAll() {
    setRefreshing(true);
    try {
      const result = await InsightsService.refreshAll(window);
      const failed = (result || []).filter((r) => !r.ok);
      setSnackbar(
        failed.length > 0
          ? { severity: "warning", message: `Synced with ${failed.length} error(s).` }
          : { severity: "success", message: "All accounts synced successfully." }
      );
      await fetchData();
    } catch (e) {
      setSnackbar({ severity: "error", message: e?.response?.data?.message || "Sync failed." });
    } finally {
      setRefreshing(false);
    }
  }

  async function handleSignOut() {
    try { await ApiService.signout(); } catch {}
    navigate("/signin");
  }

  const perAccount = useMemo(() => Array.isArray(data?.perAccount) ? data.perAccount : [], [data]);
  const socialAccounts = useMemo(() => perAccount.filter((p) => p.connectedAccount?.provider === "meta"), [perAccount]);
  const ga4Accounts = useMemo(() => perAccount.filter((p) => p.connectedAccount?.provider === "google"), [perAccount]);
  const allAccounts = perAccount;

  const safeIdx = Math.min(chartAccountIdx, Math.max(0, allAccounts.length - 1));
  const currentAcct = allAccounts[safeIdx];
  const seriesData = currentAcct?.series || {};
  const isGA4Account = currentAcct?.connectedAccount?.provider === "google";

  const SOCIAL_METRICS = [
    { key: "reach", label: "Reach" },
    { key: "impressions", label: "Impressions" },
    { key: "engagements", label: "Engagements" },
    { key: "followers", label: "Followers" },
  ];
  const GA4_METRICS = [
    { key: "sessions", label: "Sessions" },
    { key: "users", label: "Users" },
    { key: "pageViews", label: "Page views" },
    { key: "conversions", label: "Conversions" },
  ];
  const metricOptions = isGA4Account ? GA4_METRICS : SOCIAL_METRICS;

  const chartSeries = seriesData[chartMetric];
  const chartData =
    Array.isArray(chartSeries) && chartSeries.length > 0
      ? {
          xData: chartSeries.filter((p) => p.date).map((p) => new Date(p.date)),
          yData: chartSeries.filter((p) => p.date).map((p) => Number(p.value || 0)),
        }
      : null;

  const topChannels = useMemo(() => {
    const all = [];
    ga4Accounts.forEach((p) =>
      (p.topChannels || []).forEach((c) => {
        const ex = all.find((x) => x.channel === c.channel);
        if (ex) { ex.sessions += c.sessions; ex.conversions += c.conversions; }
        else all.push({ ...c });
      })
    );
    return all.sort((a, b) => b.sessions - a.sessions).slice(0, 8);
  }, [ga4Accounts]);

  const allTopPosts = useMemo(() => {
    const all = perAccount.flatMap((p) =>
      (p.topPosts || []).map((t) => ({ ...t, _account: p.connectedAccount }))
    );
    all.sort((a, b) => (b.engagedUsers + b.likes) - (a.engagedUsers + a.likes));
    return all.slice(0, 10);
  }, [perAccount]);

  const totals = data?.totals || {};
  const ga4Totals = data?.ga4Totals || {};
  const hasGA4Data = Boolean(data?.hasGA4Data);
  const isFirstLoad = loading && !data;

  const accentColor = isGA4Account ? "#EA4335" : "#5B5BD6";

  const headerActions = (
    <Button
      variant="outlined"
      size="small"
      startIcon={
        refreshing ? (
          <CircularProgress size={13} color="inherit" />
        ) : (
          <RefreshIcon sx={{ fontSize: 16 }} />
        )
      }
      onClick={handleSyncAll}
      disabled={refreshing || perAccount.length === 0}
    >
      {refreshing ? "Syncing…" : "Sync all"}
    </Button>
  );

  return (
    <AppShell account={account} onSignOut={handleSignOut} title="Overview" actions={headerActions}>
      <Stack spacing={3.5}>

        {/* Page header */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ sm: "center" }}
          justifyContent="space-between"
          spacing={2}
        >
          <Box>
            <Typography variant="h2">
              {account?.displayName ? `Hi, ${account.displayName.split(" ")[0]} 👋` : "Welcome back"}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Insights across all connected channels.
            </Typography>
          </Box>

          {/* Window selector */}
          <Stack
            direction="row"
            sx={{
              bgcolor: "#F1F5F9",
              borderRadius: 2,
              p: 0.5,
              border: "1px solid #E2E8F0",
            }}
          >
            {WINDOWS.map((w) => (
              <Box
                key={w.value}
                onClick={() => setWindow(w.value)}
                sx={{
                  px: 2,
                  py: 0.625,
                  borderRadius: 1.5,
                  cursor: "pointer",
                  fontSize: "0.8rem",
                  fontWeight: 600,
                  transition: "all 0.15s",
                  ...(window === w.value
                    ? {
                        bgcolor: "#fff",
                        color: "#5B5BD6",
                        boxShadow: "0px 1px 3px rgba(15,23,42,0.08)",
                      }
                    : { color: "#64748B", "&:hover": { color: "#1E293B" } }),
                }}
              >
                {w.label}
              </Box>
            ))}
          </Stack>
        </Stack>

        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ borderRadius: 2 }}>
            {error}
          </Alert>
        )}

        {/* Loading skeletons */}
        {isFirstLoad ? (
          <Grid container spacing={2.5}>
            {[0, 1, 2, 3].map((i) => (
              <Grid item xs={12} sm={6} md={3} key={i}>
                <Paper sx={{ p: 2.5, height: 120 }}>
                  <Skeleton height={16} width="40%" sx={{ mb: 1 }} />
                  <Skeleton height={40} width="60%" />
                </Paper>
              </Grid>
            ))}
          </Grid>
        ) : perAccount.length === 0 ? (
          <EmptyDashboard />
        ) : (
          <>
            {/* ── Social KPIs ── */}
            {socialAccounts.length > 0 && (
              <Box>
                <SectionLabel>Social · {window}</SectionLabel>
                <Grid container spacing={2.5}>
                  <Grid item xs={12} sm={6} md={3}>
                    <KpiCard
                      icon={<GroupsOutlinedIcon sx={{ fontSize: 20 }} />}
                      label="Followers"
                      value={fmt(totals.followers)}
                      accentColor="#5B5BD6"
                      accentBg="#EEF2FF"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <KpiCard
                      icon={<VisibilityOutlinedIcon sx={{ fontSize: 20 }} />}
                      label="Reach"
                      value={fmt(totals.reach)}
                      accentColor="#0EA5E9"
                      accentBg="#F0F9FF"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <KpiCard
                      icon={<FavoriteBorderIcon sx={{ fontSize: 20 }} />}
                      label="Engagements"
                      value={fmt(totals.engagements)}
                      accentColor="#EC4899"
                      accentBg="#FDF2F8"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <KpiCard
                      icon={<PercentIcon sx={{ fontSize: 20 }} />}
                      label="Engagement rate"
                      value={fmtPct(totals.engagementRate)}
                      accentColor="#10B981"
                      accentBg="#F0FDF4"
                    />
                  </Grid>
                </Grid>
              </Box>
            )}

            {/* ── GA4 KPIs ── */}
            {hasGA4Data && (
              <Box>
                <SectionLabel>Website (GA4) · {window}</SectionLabel>
                <Grid container spacing={2.5}>
                  <Grid item xs={12} sm={6} md={3}>
                    <KpiCard
                      icon={<MouseOutlinedIcon sx={{ fontSize: 20 }} />}
                      label="Sessions"
                      value={fmt(ga4Totals.sessions)}
                      accentColor="#EA4335"
                      accentBg="#FEF2F2"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <KpiCard
                      icon={<PeopleAltOutlinedIcon sx={{ fontSize: 20 }} />}
                      label="Users"
                      value={fmt(ga4Totals.users)}
                      accentColor="#F59E0B"
                      accentBg="#FFFBEB"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <KpiCard
                      icon={<VisibilityOutlinedIcon sx={{ fontSize: 20 }} />}
                      label="Page views"
                      value={fmt(ga4Totals.pageViews)}
                      accentColor="#8B5CF6"
                      accentBg="#F5F3FF"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <KpiCard
                      icon={<CheckCircleOutlineIcon sx={{ fontSize: 20 }} />}
                      label="Conversions"
                      value={fmt(ga4Totals.conversions)}
                      accentColor="#10B981"
                      accentBg="#F0FDF4"
                    />
                  </Grid>
                </Grid>
              </Box>
            )}

            {/* ── Trend chart + accounts panel ── */}
            <Grid container spacing={2.5}>
              <Grid item xs={12} md={8}>
                <Paper sx={{ overflow: "hidden" }}>
                  {/* Chart header */}
                  <Box sx={{ px: 2.5, pt: 2.5, pb: 1.5 }}>
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={1.5}
                      justifyContent="space-between"
                      alignItems={{ sm: "center" }}
                    >
                      <Box>
                        <Typography variant="h4">
                          Trend · {currentAcct?.connectedAccount?.displayName || "—"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {window} daily breakdown
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap">
                        {metricOptions.map((opt) => (
                          <Chip
                            key={opt.key}
                            label={opt.label}
                            size="small"
                            onClick={() => setChartMetric(opt.key)}
                            sx={{
                              cursor: "pointer",
                              fontSize: "0.75rem",
                              height: 26,
                              fontWeight: 600,
                              ...(chartMetric === opt.key
                                ? {
                                    bgcolor: accentColor,
                                    color: "#fff",
                                    "&:hover": { bgcolor: accentColor, opacity: 0.9 },
                                  }
                                : {
                                    bgcolor: "#F1F5F9",
                                    color: "#64748B",
                                    border: "1px solid #E2E8F0",
                                    "&:hover": { bgcolor: "#E2E8F0" },
                                  }),
                            }}
                          />
                        ))}
                      </Stack>
                    </Stack>
                  </Box>

                  {/* Account tabs */}
                  {allAccounts.length > 1 && (
                    <Tabs
                      value={safeIdx}
                      onChange={(_e, v) => {
                        setChartAccountIdx(v);
                        setChartMetric(isGA4Account ? "sessions" : "reach");
                      }}
                      sx={{ px: 2, borderBottom: "1px solid", borderColor: "divider" }}
                      variant="scrollable"
                      scrollButtons="auto"
                    >
                      {allAccounts.map((p, i) => (
                        <Tab
                          key={p.connectedAccount?.id || i}
                          label={p.connectedAccount?.displayName || `Account ${i + 1}`}
                          iconPosition="start"
                          icon={
                            <ProviderAvatar
                              kind={p.connectedAccount?.providerAccountKind}
                              src={p.connectedAccount?.avatarUrl}
                              size={20}
                            />
                          }
                          sx={{ minHeight: 48 }}
                        />
                      ))}
                    </Tabs>
                  )}

                  {/* Chart */}
                  <Box sx={{ px: 2, pb: 2, pt: 1 }}>
                    {chartData ? (
                      <LineChart
                        height={260}
                        xAxis={[{ data: chartData.xData, scaleType: "time" }]}
                        series={[
                          {
                            data: chartData.yData,
                            label: chartMetric,
                            color: accentColor,
                            area: true,
                            curve: "monotoneX",
                            showMark: false,
                          },
                        ]}
                        margin={{ left: 56, right: 16, top: 12, bottom: 32 }}
                        sx={{
                          ".MuiAreaElement-root": { fillOpacity: 0.08 },
                          ".MuiLineElement-root": { strokeWidth: 2 },
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          height: 240,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          bgcolor: "#FAFAFA",
                          borderRadius: 2,
                          border: "1px dashed #E2E8F0",
                        }}
                      >
                        <Box sx={{ textAlign: "center" }}>
                          <AutoGraphOutlinedIcon sx={{ fontSize: 32, color: "#CBD5E1", mb: 1 }} />
                          <Typography variant="body2" color="text.secondary">
                            No data — sync the account to populate.
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Paper>
              </Grid>

              {/* Connected accounts panel */}
              <Grid item xs={12} md={4}>
                <Paper sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
                  <Box sx={{ px: 2.5, pt: 2.5, pb: 1.5, borderBottom: "1px solid", borderColor: "divider" }}>
                    <Typography variant="h4">Connected accounts</Typography>
                  </Box>
                  <Box sx={{ flex: 1, overflow: "auto" }}>
                    {perAccount.length === 0 ? (
                      <Box sx={{ p: 2.5 }}>
                        <Typography variant="body2" color="text.secondary">No accounts connected.</Typography>
                      </Box>
                    ) : (
                      perAccount.map((p, idx) => (
                        <Box key={p.connectedAccount?.id}>
                          {idx > 0 && <Divider />}
                          <Stack
                            direction="row"
                            spacing={1.5}
                            alignItems="center"
                            sx={{ px: 2.5, py: 1.75 }}
                          >
                            <ProviderAvatar
                              kind={p.connectedAccount?.providerAccountKind}
                              src={p.connectedAccount?.avatarUrl}
                              size={36}
                            />
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Stack direction="row" spacing={0.75} alignItems="center">
                                <AccountStatusDot status={p.connectedAccount?.status} />
                                <Typography variant="subtitle2" noWrap sx={{ color: "text.primary" }}>
                                  {p.connectedAccount?.displayName}
                                </Typography>
                              </Stack>
                              <Typography variant="caption" color="text.secondary">
                                {p.fetchedAt ? `Synced ${fmtDateTime(p.fetchedAt)}` : "Not synced yet"}
                              </Typography>
                            </Box>
                            <Typography
                              variant="caption"
                              sx={{
                                fontWeight: 700,
                                color: "text.primary",
                                whiteSpace: "nowrap",
                                bgcolor: "#F8FAFC",
                                px: 1,
                                py: 0.5,
                                borderRadius: 1,
                                border: "1px solid #E8EDF5",
                              }}
                            >
                              {p.connectedAccount?.provider === "google"
                                ? `${fmt(p.metrics?.sessions || 0)} sess.`
                                : `${fmt(p.metrics?.followers || 0)} fans`}
                            </Typography>
                          </Stack>
                        </Box>
                      ))
                    )}
                  </Box>
                  <Box sx={{ px: 2.5, py: 1.75, borderTop: "1px solid", borderColor: "divider" }}>
                    <Link
                      component={RouterLink}
                      to="/integrations"
                      underline="none"
                      sx={{
                        fontSize: "0.8rem",
                        fontWeight: 600,
                        color: "primary.main",
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        "&:hover": { gap: 1 },
                        transition: "gap 0.15s",
                      }}
                    >
                      Manage integrations <ArrowForwardIcon sx={{ fontSize: 14 }} />
                    </Link>
                  </Box>
                </Paper>
              </Grid>
            </Grid>

            {/* ── GA4 Channel breakdown ── */}
            {topChannels.length > 0 && (
              <Paper>
                <Box sx={{ px: 2.5, pt: 2.5, pb: 0 }}>
                  <Typography variant="h4">Traffic channels</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Sessions by acquisition channel across all GA4 properties
                  </Typography>
                </Box>
                <Box sx={{ px: 1.5, pb: 1.5, pt: 1 }}>
                  <BarChart
                    height={240}
                    xAxis={[{ data: topChannels.map((c) => c.channel), scaleType: "band" }]}
                    series={[
                      { data: topChannels.map((c) => c.sessions), label: "Sessions", color: "#5B5BD6" },
                      { data: topChannels.map((c) => c.conversions), label: "Conversions", color: "#10B981" },
                    ]}
                    margin={{ left: 56, right: 16, top: 16, bottom: 72 }}
                    sx={{
                      ".MuiChartsAxis-tickLabel": { fontSize: "0.72rem", fill: "#64748B" },
                      ".MuiBarElement-root": { rx: 4 },
                    }}
                  />
                </Box>
              </Paper>
            )}

            {/* ── Top content table ── */}
            <Card>
              <Box
                sx={{
                  px: 2.5,
                  py: 2,
                  borderBottom: "1px solid",
                  borderColor: "divider",
                  bgcolor: "#FAFBFC",
                }}
              >
                <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ sm: "center" }} spacing={1}>
                  <Box>
                    <Typography variant="h4">Top content</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Best-performing across {perAccount.length} account{perAccount.length !== 1 ? "s" : ""} · {window}
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip
                      icon={<TrendingUpIcon sx={{ fontSize: "14px !important" }} />}
                      label="By engagement"
                      size="small"
                      variant="outlined"
                      sx={{ bgcolor: "#fff" }}
                    />
                    <ExportButton
                      rows={allTopPosts}
                      filename={`top-content-${window}.csv`}
                      columns={[
                        { label: "Account", value: (r) => r._account?.displayName },
                        { label: "Caption", value: (r) => r.message },
                        { label: "Date", value: (r) => r.createdAt },
                        { label: "Likes", value: (r) => r.likes },
                        { label: "Comments", value: (r) => r.comments },
                        { label: "Impressions", value: (r) => r.impressions },
                        { label: "Engaged users", value: (r) => r.engagedUsers },
                        { label: "Link", value: (r) => r.permalink },
                      ]}
                    />
                  </Stack>
                </Stack>
              </Box>

              <Box sx={{ overflowX: "auto" }}>
                {allTopPosts.length === 0 ? (
                  <Box sx={{ p: 4, textAlign: "center" }}>
                    <Typography variant="body2" color="text.secondary">
                      No posts yet — sync an account to populate.
                    </Typography>
                  </Box>
                ) : (
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Content</TableCell>
                        <TableCell>Channel</TableCell>
                        <TableCell align="right">Likes</TableCell>
                        <TableCell align="right">Comments</TableCell>
                        <TableCell align="right">Reach / Views</TableCell>
                        <TableCell align="right"></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {allTopPosts.map((p, idx) => (
                        <TableRow key={`${p._account?.id}-${p.id}-${idx}`} hover>
                          <TableCell sx={{ maxWidth: 340, py: 1.5 }}>
                            <Stack direction="row" spacing={1.5} alignItems="center">
                              {p.thumbnailUrl ? (
                                <Box
                                  component="img"
                                  src={p.thumbnailUrl}
                                  alt=""
                                  sx={{
                                    width: 48,
                                    height: 48,
                                    borderRadius: 1.5,
                                    objectFit: "cover",
                                    flexShrink: 0,
                                    border: "1px solid #E8EDF5",
                                  }}
                                />
                              ) : (
                                <Box
                                  sx={{
                                    width: 48,
                                    height: 48,
                                    bgcolor: "#F1F5F9",
                                    borderRadius: 1.5,
                                    flexShrink: 0,
                                    border: "1px solid #E8EDF5",
                                  }}
                                />
                              )}
                              <Box sx={{ minWidth: 0 }}>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    display: "-webkit-box",
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: "vertical",
                                    overflow: "hidden",
                                    fontWeight: 500,
                                    color: "text.primary",
                                    lineHeight: 1.45,
                                  }}
                                >
                                  {p.message || <em style={{ color: "#94A3B8" }}>(no caption)</em>}
                                </Typography>
                                {p.createdAt && (
                                  <Typography variant="caption" color="text.secondary">
                                    {fmtDateTime(p.createdAt)}
                                  </Typography>
                                )}
                              </Box>
                            </Stack>
                          </TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <ProviderAvatar
                                kind={p._account?.providerAccountKind}
                                src={p._account?.avatarUrl}
                                size={26}
                              />
                              <Typography variant="body2" noWrap sx={{ maxWidth: 110, fontWeight: 500 }}>
                                {p._account?.displayName}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{fmt(p.likes)}</Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2">{fmt(p.comments)}</Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2">{fmt(p.impressions)}</Typography>
                          </TableCell>
                          <TableCell align="right">
                            {p.permalink && (
                              <Link
                                href={p.permalink.startsWith("http") ? p.permalink : `https://yoursite.com${p.permalink}`}
                                target="_blank"
                                rel="noreferrer"
                                sx={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                  fontSize: "0.8rem",
                                  fontWeight: 600,
                                  color: "primary.main",
                                  textDecoration: "none",
                                  "&:hover": { textDecoration: "underline" },
                                }}
                              >
                                Open <OpenInNewIcon sx={{ fontSize: 12 }} />
                              </Link>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </Box>
            </Card>
          </>
        )}
      </Stack>

      <Snackbar
        open={Boolean(snackbar)}
        autoHideDuration={4000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        {snackbar ? (
          <Alert severity={snackbar.severity} onClose={() => setSnackbar(null)} variant="filled" sx={{ borderRadius: 2 }}>
            {snackbar.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </AppShell>
  );
}
